import { useRouter } from 'next/router'
import { useTranslation } from '@mc/i18n'
import { useCohortDetails } from '../api-hooks/enrollment/useCohortDetails'
import { paths } from '../utils/paths'
import { resolveEffectiveWindowType } from '../utils/resolveEnrollmentWindow'

export interface CtaItem {
  label: string
  href: string
}

export interface EnrollmentCtaConfig {
  primaryCta: CtaItem
  secondaryCta: CtaItem
  /** True when the primary CTA is "Log In" — used to gate educatorPurchaseClick. */
  isPrimaryLogIn: boolean
}

/**
 * Resolves primary/secondary CTA label + href for all enrollment surfaces via
 * `resolveEffectiveWindowType` — the same resolver as the hero strip, so the two
 * can never diverge. Reads cohort-details directly rather than useEnrollmentBanner
 * (banner and CTA concerns are independent).
 */
export const useEnrollmentCtaConfig = (): EnrollmentCtaConfig => {
  const { t } = useTranslation('@mc/educator')
  const router = useRouter()
  const { data: cohortDetails } = useCohortDetails()

  const windowType = resolveEffectiveWindowType({
    windowType: cohortDetails?.currentWindow?.windowType,
    state: cohortDetails?.state,
  })

  const isSubjectPage = router.asPath.startsWith(paths.subjects)
  const loginHref = isSubjectPage
    ? paths.login
    : `${paths.login}?returnTo=${encodeURIComponent(router.asPath)}`

  const applyForFree: CtaItem = {
    label: t('enrollment.cta.applyForFree'),
    href: paths.enrollmentApplication,
  }
  const joinWaitlist: CtaItem = {
    label: t('enrollment.cta.joinWaitlist'),
    href: paths.enrollmentWaitlist,
  }
  const logIn: CtaItem = {
    label: t('enrollment.cta.logIn'),
    href: loginHref,
  }

  if (windowType === 'final_pay_by') {
    return {
      primaryCta: logIn,
      secondaryCta: joinWaitlist,
      isPrimaryLogIn: true,
    }
  }

  if (windowType === 'closed') {
    return {
      primaryCta: joinWaitlist,
      secondaryCta: logIn,
      isPrimaryLogIn: false,
    }
  }

  // early/priority/general enrollment or undefined (API error / default)
  return {
    primaryCta: applyForFree,
    secondaryCta: logIn,
    isPrimaryLogIn: false,
  }
}
